// #region: Class definition *********************************************************************/
/**
 * Date and time utility class providing parsing, formatting, and validation functionality.
 * Contains all date/time related code with support for custom formats and timezone handling.
 * 
 * @class Dating
 */
window.dating = new class {

    // #region: Private Fields *******************************************************************/
    /**
     * Regular expressions for parsing date/time tokens.
     * @type {Object}
     * @private
     * @memberof Dating
     */
    #expressions;
    
    /**
     * Regular expression for matching formatting tokens.
     * @type {RegExp}
     * @private
     * @memberof Dating
     */
    #formattingTokens;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Initializes the dating utility with default expressions and formatting tokens.
     * Sets up magic numbers for year validation and creates parsing expressions.
     * 
     * @constructor
     * @memberof Dating
     */
    constructor() {
        // Magic numbers.
        this.MINYEAR = 1800;
        this.MAXYEAR = 2200;

        // Local regex expressions for format matching.
        const match1 = /\d/ // 0 - 9
        const match2 = /\d\d/ // 00 - 99
        const match3 = /\d{3}/ // 000 - 999
        const match4 = /\d{4}/ // 0000 - 9999
        const match1to2 = /\d\d?/ // 0 - 99
        const matchSigned = /[+-]?\d+/ // -inf - inf
        const matchOffset = /[+-]\d\d:?(\d\d)?|Z/ // +00:00 -00:00 +0000 or -0000 +00 or Z
        const matchWord = /\d*[^-_:/,()\s\d]+/ // Word

        // Local function to create/calculate property values in the datetime object (array).
        const addInput = function (property) {
            return function (input) {
                this[property] = +input;
            }
        };

        // Local function to check for a present meridiem indicator, being in the afternoon.
        const meridiemMatch = (input, isLowerCase) => {
            return input === (isLowerCase ? "pm" : "PM");
        };

        // Local function to fix two digit year display.
        const parseTwoDigitYear = (input) => {
            input = +input;
            return input + (input > 68 ? 1900 : 2000);
        };

        // Local function to parse timezone offset from string.
        const offsetFromString = (str) => {
            if (str === 'Z') {
                return 0;
            }

            const m = str.match(/^([+-])(\d{2}):?(\d{2})?$/);

            if (!m) {
                return 0;
            }

            const sign = m[1] === '+' ? 1 : -1;
            const hh = parseInt(m[2], 10);
            const mm = parseInt(m[3] || '0', 10);

            return sign * (hh * 60 + mm);
        };

        // Local function to parse time zones.
        const zoneExpressions = [matchOffset, function (input) {
            const zone = this.zone || (this.zone = {});
            zone.offset = offsetFromString(input);
        }];

        /**
         * Regular expression pattern for matching all date/time format tokens.
         * @type {RegExp}
         * @memberof Dating
         */
        this.#formattingTokens = /(\[[^[]*\])|([-_:\/.,()\s]+)|(A|a|yyyy|yy?|MM?|dd?|hh?|HH?|mm?|ss?|S{1,3}|z|ZZ?)/g;
        this.#expressions = {
            A: [matchWord, function (input) {
                this.afternoon = meridiemMatch(input, false);
            }],
            a: [matchWord, function (input) {
                this.afternoon = meridiemMatch(input, true);
            }],
            S: [match1, function (input) {
                this.milliseconds = +input * 100;
            }],
            SS: [match2, function (input) {
                this.milliseconds = +input * 10;
            }],
            SSS: [match3, function (input) {
                this.milliseconds = +input;
            }],
            s: [match1to2, addInput("seconds")],
            ss: [match1to2, addInput("seconds")],
            m: [match1to2, addInput("minutes")],
            mm: [match1to2, addInput("minutes")],
            H: [match1to2, addInput("hours")],
            h: [match1to2, addInput("hours")],
            HH: [match1to2, addInput("hours")],
            hh: [match1to2, addInput("hours")],
            d: [match1to2, addInput("day")],
            dd: [match2, addInput("day")],
            M: [match1to2, addInput("month")],
            MM: [match2, addInput("month")],
            y: [matchSigned, addInput("year")],
            yy: [match2, function (input) {
                this.year = parseTwoDigitYear(input);
            }],
            yyyy: [match4, addInput("year")],
            Z: zoneExpressions,
            ZZ: zoneExpressions
        };
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Returns the string representation of this class instance.
     * @returns {string} The string representation of this class
     * @memberof Dating
     */
    toString() {
        return "[dating]";
    }

    /**
     * Returns the string representation of this class.
     * @returns {string} The string representation of this class
     * @static
     * @memberof Dating
     */
    static toString() {
        return "[dating]";
    }

    /**
     * Returns the version number of this class.
     * @returns {string} The version number of this class
     * @static
     * @memberof Dating
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Formats an ISO date string according to the specified format.
     * @param {string} date The ISO date string to parse
     * @param {string} [format=null] The format to use for output
     * @returns {string} The formatted date string, or empty string if invalid
     * @memberof Dating
     */
    formatDate(date, format = null) {
        const dateObject = (date instanceof Date) ? date : (!isNaN(Date.parse(date)) ? new Date(date) : null);

        if (dateObject) {
            const formatString = format ?? "d-M-yyyy";

            // Local function to parse a time zone.
            const padZone = (instance) => {
                const negMinutes = -instance.getTimezoneOffset();
                const minutes = Math.abs(negMinutes);
                const hourOffset = Math.floor(minutes / 60);
                const minuteOffset = minutes % 60;

                return `${negMinutes >= 0 ? "+" : "-"}${String(hourOffset).padStart(2, "0")}:${String(minuteOffset).padStart(2, "0")}`;
            };

            // Local function to parse AM/PM display.
            const meridiemFunc = (hour, isLowercase) => {
                const meridiem = (hour < 12 ? "AM" : "PM");

                return isLowercase ? meridiem.toLowerCase() : meridiem;
            };

            // Create the separate parts based on the format.
            const year = dateObject.getFullYear();
            const month = dateObject.getMonth();
            const day = dateObject.getDate();
            const hours = dateObject.getHours();
            const minutes = dateObject.getMinutes();
            const seconds = dateObject.getSeconds();
            const milliseconds = dateObject.getMilliseconds();
            const zone = padZone(dateObject);

            const matches = (match) => {
                switch (match) {
                    case "a":
                        return meridiemFunc(hours, true);

                    case "A":
                        return meridiemFunc(hours, false);

                    case "yy":
                    case "YY":
                        return String(year).slice(-2);

                    case "yyyy":
                    case "YYYY":
                        return String(year).padStart(4, "0");

                    case "M":
                        return (month + 1);

                    case "MM":
                        return String(month + 1).padStart(2, "0");

                    case "d":
                        return day;

                    case "dd":
                        return String(day).padStart(2, "0");

                    case "H":
                        return String(hours);

                    case "HH":
                        return String(hours).padStart(2, "0");

                    case "h":
                        return String((hours % 12) || 12);

                    case "hh":
                        return String((hours % 12) || 12).padStart(2, "0");

                    case "m":
                        return String(minutes);

                    case "mm":
                        return String(minutes).padStart(2, "0");

                    case "s":
                        return String(seconds);

                    case "ss":
                        return String(seconds).padStart(2, "0");

                    case "SSS":
                        return String(milliseconds).padStart(3, "0");

                    case "z":
                        return "GMT" + zone.replace(":", "");

                    case "Z":
                        // e.g. +02:00.
                        return zone;

                    case "ZZ":
                        // e.g. +0200.
                        return zone.replace(":", "");

                    default:
                        break
                }

                return null;
            }

            return formatString.replace(this.#formattingTokens, (match, $1) => $1 || matches(match) || match);
        }

        // Not a valid date.
        return "";
    }

    /**
     * Formats a date string to display as human readable date time (losing the seconds, using dashes).
     * @param {string} dateString The date string to format
     * @returns {string} The formatted date time string
     * @memberof Dating
     */
    formatDateTime(dateString) {
        const parsedDate = new Date(dateString);

        if (isNaN(parsedDate.getTime())) {
            return dateString;
        }

        const date = [parsedDate.getDate(), parsedDate.getMonth() + 1, parsedDate.getFullYear()].join("-");
        const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
        const time = `${parsedDate.getHours()}:${minutes}`;

        return `${date} ${time}`;
    }

    /**
     * Converts a date string to a JavaScript Date object.
     * @param {string} dateString The string to parse to a date object
     * @returns {Date} The parsed date when successful, minimum date when failed
     * @memberof Dating
     */
    getDateFromString(dateString) {
        let myDate = new Date();

        try {
            // Remove extra spaces to prevent issues in parsing.
            dateString = dateString.trim();

            const initialTimeSeparatorPosition = dateString.indexOf(":");

            if (initialTimeSeparatorPosition > -1 && initialTimeSeparatorPosition <= 3) {
                // Is time only. Add date to help parsing.
                dateString = "01-01-1900 " + dateString;
            }

            const firstDashPosition = dateString.indexOf("-");
            const secondDashPosition = dateString.indexOf("-", firstDashPosition + 1);
            const spacePosition = dateString.indexOf(" ");
            const hasTime = spacePosition > -1;
            const strDay = dateString.substring(0, firstDashPosition);
            const strMonth = dateString.substring(firstDashPosition + 1, secondDashPosition);
            let strYear;

            if (hasTime) {
                strYear = dateString.substring(secondDashPosition + 1, spacePosition);
            }
            else {
                strYear = dateString.substring(secondDashPosition + 1);
            }

            const timeString = dateString.substring(spacePosition);
            const timeSeparatorPosition = timeString.indexOf(":");
            const strHour = timeString.substring(0, timeSeparatorPosition);
            const strMinute = timeString.substring(timeSeparatorPosition + 1);

            const day = parseInt(strDay, 10);
            const month = parseInt(strMonth, 10);
            const year = parseInt(strYear, 10);
            const hour = hasTime ? parseInt(strHour, 10) : 0;
            const minute = hasTime ? parseInt(strMinute, 10) : 0;

            myDate.setFullYear(year, month - 1, day);
            myDate.setHours(hour, minute, 0, 0);
        }
        catch (e) {
            myDate = new Date(1900, 1, 1);
        }

        return myDate;
    }

    /**
     * Validates if a string can be evaluated as a date, time, or combination of both.
     * @param {string} dateString The string to validate
     * @param {string} format The format to use: 'date', 'time', or 'both'
     * @returns {string} Empty string when valid, error message when invalid
     * @memberof Dating
     */
    isDateStr(dateString, format) {
        dateString = dateString.trim();

        // If no content is given to validate, return 'Invalid date'.
        if (!dateString) {
            return resources.validationMessages.invalidDate;
        }

        let isDateOnly = format === "date";
        let isTimeOnly = format === "time";
        let isBoth = format === "both";

        let dateAndTime = dateString.split(" "),
            hasBoth = dateAndTime.length === 2,
            hasDate = dateString.indexOf("-") > -1,
            hasTime = dateString.indexOf(":") > -1,
            date = hasBoth ? dateAndTime[0] : dateString,
            time = hasBoth ? dateAndTime[1] : dateString;

        if (dateAndTime.length > 2 || ((isDateOnly || isTimeOnly) && hasBoth) || (!hasDate && !hasTime)) {
            return isTimeOnly ? resources.validationMessages.invalidTime : resources.validationMessages.invalidDate;
        }

        if (isBoth || isDateOnly || hasBoth || hasDate) {
            let dateParts = date.split("-");

            if (dateParts.length !== 3) {
                return resources.validationMessages.invalidDate;
            }

            let dayString = dateParts[0];
            let monthString = dateParts[1];
            let yearString = dateParts[2];
            let day = Number(dayString);
            let month = Number(monthString);
            let year = Number(yearString);

            // Validate month first.
            if (!this.#isWithin(monthString.length, 1, 2) || !this.#isWithin(month, 1, 12)) {
                return resources.validationMessages.invalidMonth;
            }

            // Validate year next.
            if (yearString.length !== 4 || !this.#isWithin(year, this.MINYEAR, this.MAXYEAR)) {
                return resources.validationMessages.invalidYear.replace("{minyear}", this.MINYEAR).replace("{maxyear}", this.MAXYEAR);
            }

            // Now compute daysInMonth safely.
            const daysInMonths = [31, this.#daysInFebruary(year), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
            const daysInMonth = daysInMonths[month - 1];

            // Finally validate day.
            if (!this.#isWithin(dayString.length, 1, 2) || !this.#isWithin(day, 1, daysInMonth)) {
                return resources.validationMessages.invalidDay;
            }
        }

        if (isBoth || isTimeOnly || hasBoth || hasTime) {
            let timeParts = time.split(":");

            if (timeParts.length !== 2) {
                return resources.validationMessages.invalidTime;
            }

            let hoursString = timeParts[0],
                minutesString = timeParts[1],
                hours = Number(hoursString);
            let minutes = Number(minutesString);

            if (!this.#isWithin(hoursString.length, 1, 2) || !this.#isWithin(hours, 0, 23)) {
                return resources.validationMessages.invalidTime;
            }

            if (minutesString.length !== 2 || !this.#isWithin(minutes, 0, 59)) {
                return resources.validationMessages.invalidTime;
            }
        }

        return "";
    }
    
    /**
     * Parses a date string to a valid datetime string representation.
     * @param {string} datestring The date string to parse
     * @param {string} [format=null] The format that the input string should follow
     * @returns {string} A standard ISO 8601 string
     * @memberof Dating
     */
    parseDateString(datestring, format = null) {
        try {
            // The parser will return a function that can parse the provided string.
            const parser = this.#makeParser(format ?? "d-M-yyyy");
            const parsedDate = parser(datestring);

            // Create the separate parts based on the format.
            let { year, month, day, hours, minutes, seconds, milliseconds, zone } = parsedDate;
            const now = new Date();

            // Day default to 1.
            day ??= ((!year && !month) ? now.getDate() : 1);

            // Month in javascript is 1 off (if we need to get it from the date object).
            month ??= now.getMonth() + 1;
            year ??= now.getFullYear();
            hours ??= 0;
            minutes ??= 0;
            seconds ??= 0;
            milliseconds ??= 0;

            // Check if we can create a valid date from this data.
            // Create a standard ISO 8601 string so we can do a Date.parse for a valid date.
            // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse;
            // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/padStart
            let parsedDateString = `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}T${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${milliseconds.toString().padStart(3, "0")}`;

            if (zone) {
                const sign = zone.offset >= 0 ? '+' : '-';
                const abs = Math.abs(zone.offset);
                const hh = String(Math.floor(abs / 60)).padStart(2, '0');
                const mm = String(abs % 60).padStart(2, '0');

                parsedDateString += `${sign}${hh}:${mm}`;
            }

            return parsedDateString;
        }
        catch {
            // Not possible to parse the string, return original string.
            return datestring;
        }
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Corrects hour values for 12-hour (AM/PM) display format.
     * @param {Object} datetime The datetime object to correct for meridiem setting
     * @private
     * @memberof Dating
     */
    #correctHours(datetime) {
        const { afternoon } = datetime;

        if (afternoon !== undefined) {
            const current = Number(datetime.hours ?? 0);

            if (afternoon) {
                datetime.hours = current < 12 ? current + 12 : current;
            }
            else if (current === 12) {
                datetime.hours = 0;
            }

            delete datetime.afternoon;
        }
    }

    /**
     * Determines if a given year is a leap year (February has 29 days).
     * @param {number} year The year to check
     * @returns {number} The number of days in February (28 or 29)
     * @private
     * @memberof Dating
     */
    #daysInFebruary(year) {
        // February has 29 days in any year evenly divisible by four, except for centurial years which are not also divisible by 400.
        return (((year % 4 === 0) && ((!(year % 100 === 0)) || (year % 400 === 0))) ? 29 : 28);
    }

    /**
     * Checks if a number is within a specified range (inclusive).
     * @param {number} number The number to validate
     * @param {number} min The minimum value (inclusive)
     * @param {number} max The maximum value (inclusive)
     * @returns {boolean} True when within the range, false otherwise
     * @private
     * @memberof Dating
     */
    #isWithin(number, min, max) {
        return number >= min && number <= max;
    }

    /**
     * Creates a parser function that can evaluate date/time strings based on a given format.
     * @param {string} format The format to use for parsing
     * @returns {Function} A parser function that takes an input string and returns a datetime object
     * @private
     * @memberof Dating
     */
    #makeParser(format) {
        const array = format.match(this.#formattingTokens);
        const { length } = array;

        for (let i = 0; i < length; i += 1) {
            const token = array[i];
            const parseTo = this.#expressions[token];
            const regex = parseTo && parseTo[0];
            const parser = parseTo && parseTo[1];

            if (parser) {
                array[i] = { regex, parser };
            }
            else {
                array[i] = token.replace(/^\[|\]$/g, "");
            }
        }

        return (input) => {
            const datetime = {};

            for (let i = 0, start = 0; i < length; i += 1) {
                const token = array[i]
                if (typeof token === "string") {
                    start += token.length;
                }
                else {
                    const { regex, parser } = token;
                    const part = input.slice(start);
                    const match = regex.exec(part);

                    if (!match) {
                        throw new Error(`Invalid date format: unable to parse part "${part}"`);
                    }

                    const value = match[0];
                    parser.call(datetime, value);
                    input = input.replace(value, "");
                }
            }

            this.#correctHours(datetime);

            return datetime
        }
    }
    // #endregion ********************************************************************************/

}();
// #endregion ************************************************************************************/
